import { useCallback, useEffect, useRef, useState } from 'react'
import { agentToolLabel } from './agentDisplay'
import { useHorizontalWheelScroll } from '../../hooks/useHorizontalWheelScroll'
import type { AgentTimelineImage } from '../../lib/agentTimelineMapper'
import { useImageUrlSelfHeal } from '../../hooks/useImageUrlSelfHeal'
import { useImageLightbox } from '../../context/ImageLightboxContext'
import { classifyReferenceImageLink } from '../../lib/referenceImageUrl'

type AgentImageStripProps = {
  toolName: string
  images: AgentTimelineImage[]
}

/**
 * 单张自愈缩略图：七牛私有桶签名链仅 30 分钟有效，加载失败时按 objectKey
 * 走 refresh-url 即时补签（与图库页同一套自愈链路）；补签仍失败才显示占位。
 * stripVisible=false 时只渲染占位盒，不发起图片请求——图片条随运行不断累积，
 * 全量直出会把带宽和内存打爆，必须等滚动进入视口才真正加载。
 * 注意：占位与图片都必须 shrink-0，否则失败态占位会被 flex 压缩，
 * 容器失去横向溢出，左右箭头永远不会出现。
 *
 * 防盗链：七牛 CDN 的 Referer 白名单通常不含本地开发域（localhost:5173），
 * <img> 请求带 Referer 会被 403 拒绝，而锚点 rel="noreferrer" 新开页却能打开
 * ——表现为「点击能看、组件裂图」。与 ThemedImage 同策略：remote-cdn 一律 no-referrer。
 */
function AgentImageThumb({
  image,
  stripVisible,
  index,
  onOpen,
}: {
  image: AgentTimelineImage
  stripVisible: boolean
  index: number
  onOpen: (index: number) => void
}) {
  const { displayUrl, failed, healing, reloadTick, handleError, handleLoad } = useImageUrlSelfHeal({
    rawUrl: stripVisible ? image.src : undefined,
    objectKey: image.objectKey,
  })

  const placeholderClass = [
    'flex h-24 w-32 shrink-0 items-center justify-center rounded-lg text-[11px] text-ink-soft sm:h-28',
    'ring-1 ring-[color-mix(in_srgb,var(--line)_60%,transparent)]',
    'bg-[color-mix(in_srgb,var(--surface-muted)_60%,transparent)]',
  ].join(' ')

  if (!stripVisible) {
    return <span aria-hidden className={placeholderClass} />
  }

  if (failed) {
    return (
      <span aria-label={image.alt || '图片加载失败'} className={placeholderClass}>
        {healing ? '正在恢复…' : '图片暂不可用'}
      </span>
    )
  }

  return (
    <button
      type="button"
      className="group flex shrink-0 cursor-zoom-in items-center"
      title={image.alt || '查看原图'}
      aria-label={`查看第 ${index + 1} 张图片`}
      onClick={() => onOpen(index)}
    >
      <img
        key={reloadTick}
        src={displayUrl}
        alt={image.alt || '智能体引用图片'}
        loading="lazy"
        decoding="async"
        referrerPolicy={
          classifyReferenceImageLink(displayUrl) === 'remote-cdn' ? 'no-referrer' : undefined
        }
        onError={image.objectKey ? handleError : undefined}
        onLoad={handleLoad}
        className={[
          'h-24 w-auto max-w-[200px] rounded-lg object-cover sm:h-28',
          'ring-1 ring-[color-mix(in_srgb,var(--line)_60%,transparent)]',
          'transition group-hover:opacity-90',
        ].join(' ')}
      />
    </button>
  )
}

/**
 * 智能体时间线图片条：展示工具返回的生图结果 / 引用图。
 * 宽度略小于正文（92%），图片按原始比例固定高度横向排列，超出部分横向滚动；
 * 两侧翻页按钮与滚动区同行 flex（items-center）布局居中，描边 + 半透明面板 + 背景模糊。
 * 点击缩略图打开全局灯箱预览（带左右切换 / 缩放 / 补签自愈），不再新开页面。
 *
 * 滚轮：容器 overflow-x auto 会让 overflow-y 计算为 auto，纵向滚轮被它吃掉
 * （.scroll-region 的 overscroll-behavior: contain 又切断了滚动链）。
 * 沿用项目横向条的既有方案：scroll-x-wheel 覆盖回 auto + useHorizontalWheelScroll(false)
 * 只做溢出记账，不拦截纵向滚轮，页面照常上下滚。
 * 原生横向滚动条视觉噪音大，隐藏后由两侧按钮 + 滚轮承担导航。
 */
export function AgentImageStrip({ toolName, images }: AgentImageStripProps) {
  const lightbox = useImageLightbox()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const stripRef = useRef<HTMLElement>(null)
  const overflowRef = useHorizontalWheelScroll<HTMLDivElement>({ mapVerticalWheelToHorizontal: false })
  const [inViewport, setInViewport] = useState(false)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  // 进入视口才开始加载图片：时间线可能堆叠多个图片条，全量直出带宽压力过大
  useEffect(() => {
    const el = stripRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInViewport(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInViewport(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const syncArrows = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    setCanLeft(el.scrollLeft > 4)
    setCanRight(maxScroll > 4 && el.scrollLeft < maxScroll - 4)
  }, [])

  useEffect(() => {
    syncArrows()
    const el = scrollRef.current
    if (!el) return
    window.addEventListener('resize', syncArrows)
    // 图片异步加载/自愈重签会改变内容宽度，仅在 mount 时 sync 会漏判箭头状态
    const observer = new ResizeObserver(syncArrows)
    observer.observe(el)
    return () => {
      window.removeEventListener('resize', syncArrows)
      observer.disconnect()
    }
  }, [syncArrows, images.length])

  const scrollByAmount = (direction: 1 | -1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: direction * Math.max(240, el.clientWidth * 0.8), behavior: 'smooth' })
  }

  /**
   * 翻页箭头：与滚动区同属一行 flex（items-center），靠布局引擎垂直居中。
   * 不用 absolute top-1/2 —— 那依赖"容器高度 === 图片行高度"这一脆弱前提，
   * 任何多余高度（滚动条、行内基线、内边距）都会让中心偏移。
   * 两侧常驻占位：不可用时 invisible + 禁用，避免出现/消失造成横向抖动。
   */
  function renderArrow(side: 'left' | 'right') {
    const canScroll = side === 'left' ? canLeft : canRight
    return (
      <button
        type="button"
        aria-label={side === 'left' ? '上一张' : '下一张'}
        disabled={!canScroll}
        onClick={() => scrollByAmount(side === 'left' ? -1 : 1)}
        className={[
          // 与灯箱「上一张/下一张」同一套外观：描边 + 半透明面板 + 背景模糊 + 同尺寸 chevron
          'shrink-0 rounded-lg border border-line/80 bg-[color-mix(in_srgb,var(--panel)_92%,transparent)]',
          'p-1.5 text-ink shadow-sm backdrop-blur-[2px] transition hover:opacity-90',
          'disabled:pointer-events-none',
          canScroll ? 'visible opacity-100' : 'invisible opacity-0',
        ].join(' ')}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d={side === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
        </svg>
      </button>
    )
  }

  const openLightbox = useCallback(
    (index: number) => {
      lightbox.open(
        images.map((image) => ({
          src: image.src,
          alt: image.alt ?? '',
          prompt: image.alt,
          objectKey: image.objectKey,
          taskLabel: agentToolLabel(toolName),
        })),
        { index },
      )
    },
    [images, lightbox, toolName],
  )

  if (!images.length) return null
  return (
    <figure ref={stripRef} className="agent-image-strip w-[92%] min-w-0">
      <figcaption className="mb-1 text-[11px] font-medium text-ink-soft">
        {agentToolLabel(toolName)} · {images.length} 张图片
      </figcaption>
      <div className="flex items-center gap-1.5">
        {renderArrow('left')}
        <div
          ref={(node) => {
            scrollRef.current = node
            overflowRef(node)
          }}
          onScroll={syncArrows}
          className="scroll-region scroll-x-wheel min-w-0 flex-1 flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((image, index) => (
            <AgentImageThumb
              key={`${image.src}-${index}`}
              image={image}
              index={index}
              stripVisible={inViewport}
              onOpen={openLightbox}
            />
          ))}
        </div>
        {renderArrow('right')}
      </div>
    </figure>
  )
}
